import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AxiosError } from 'axios';
import { useDebounce } from 'hooks/useDebounce';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import { URLS } from 'services/urls';
import { useSignUpUserAndOrg, useSignUpOrgNameValidate, useValidateSignup } from './hooks';
import { useSignUpUserAndOrgValidate } from './hooks/useSignUpUserAndOrgValidate';
import { IValidateSignupResponse } from './hooks/useValidateSignup';
import { SignUpType, IAuth0SignUpData } from '../../../auth';
import { useSignUpContext, SignUpStage } from './SignUpContext';
import { SocialSignUpConnection } from './constants';
import {
    PERSONAL_EMAIL_NOT_ALLOWED_MESSAGE,
    PERSONAL_EMAIL_NOT_ALLOWED_CODE,
    BLOCKED_DOMAIN_CODE,
    BLOCKED_DOMAIN_MESSAGE,
} from './signUpValidationConstants';

export interface SignUpField {
    value: string;
    errorMsg: string | null;
}

export interface ISignUpManager {
    signUpEnabled: boolean;
    socialSignUpEnabled: boolean;
    email: SignUpField;
    jobTitle: SignUpField;
    organizationName: SignUpField;
    name: SignUpField;
    hasAcceptedTerms: boolean;
    validCaptcha: boolean;
    isOrgNameValid: boolean;
    orgValidationSuccess: boolean;
    userOrgLookupData?: IValidateSignupResponse;
    redirectUrlAfterPasswordReset?: string;
    socialSignUpConnectionType?: SocialSignUpConnection;
    setHasAcceptedTerms: React.Dispatch<React.SetStateAction<boolean>>;
    setRedirectUrlAfterPasswordReset: React.Dispatch<React.SetStateAction<string | undefined>>;
    handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleJobTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleOrganizationNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSignUp: () => void;
    handleSocialSignUp: (connection: SocialSignUpConnection) => void;
    resetSignUpState?: () => void;
}

const useSignUpManager = (): ISignUpManager => {
    const [searchParams] = useSearchParams();
    const emailFromUrl = searchParams.get('email');

    const [email, setEmail] = useState<SignUpField>({ value: emailFromUrl ?? '', errorMsg: null });
    const [jobTitle, setJobTitle] = useState<SignUpField>({ value: '', errorMsg: null });
    const [organizationName, setOrganizationName] = useState<SignUpField>({ value: '', errorMsg: null });
    const [name, setName] = useState<SignUpField>({ value: '', errorMsg: null });
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(true); // Default to true. Currently creating account from deal accepts terms by default.
    const [validCaptcha, setValidCaptcha] = useState(true);
    const [blockSignUp, setBlockSignUp] = useState(false); // Block sign-up for actions like typing org name
    const [redirectUrlAfterPasswordReset, setRedirectUrlAfterPasswordReset] = useState<string | undefined>();
    const [socialSignUpConnectionType, setSocialSignUpConnectionType] = useState<SocialSignUpConnection | undefined>();

    const [signUpAuth0Data, setSignUpAuth0Data] = useLocalStorage<IAuth0SignUpData>(PageSpecificKeys.SignUpAuth0Data, {});

    const { setCurrentStage, updateStageData, isDirectSignUpFlow } = useSignUpContext();

    const {
        loginWithRedirect,
        logout,
    } = useAuth0();

    const resetSignUpState = () => {
        setEmail({ value: '', errorMsg: null });
        setJobTitle({ value: '', errorMsg: null });
        setOrganizationName({ value: '', errorMsg: null });
        setName({ value: '', errorMsg: null });
        setSocialSignUpConnectionType(undefined);
        setSignUpAuth0Data({});
        // Log out from Auth0 to reset any social sign-up state
        logout({ openUrl: false });
    };

    // Debounced values
    const debouncedOrganizationName = useDebounce(organizationName.value, 500);
    const debouncedEmail = useDebounce(email.value, 500);

    const isValidEmailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(debouncedEmail);

    // Look up existing user or organization when valid email is entered
    const { data: userOrgLookupData } = useValidateSignup(
        debouncedEmail,
        {
            enabled: isValidEmailFormat && !!debouncedEmail,
        },
    );

    const { data: { isValid: isOrgNameValid = true } = {}, isLoading: validatingOrgName } = useSignUpOrgNameValidate(
        debouncedOrganizationName,
        {
            enabled: !!debouncedOrganizationName && !userOrgLookupData?.org,
        },
    );

    const orgValidationSuccess = !validatingOrgName && isOrgNameValid && !!debouncedOrganizationName && !blockSignUp;

    const { mutate: signUpUserAndOrg } = useSignUpUserAndOrg();

    const { mutateAsync: validateSignUpUserAndOrg } = useSignUpUserAndOrgValidate();

    const { executeRecaptcha } = useGoogleReCaptcha();

    useEffect(() => {
        setBlockSignUp(false);
    }, [debouncedOrganizationName]);

    useEffect(() => {
        if (userOrgLookupData?.org) {
            setOrganizationName(prev => ({ ...prev, errorMsg: null }));
            return;
        }

        if (debouncedOrganizationName) {
            if (!validatingOrgName && !isOrgNameValid) {
                setOrganizationName(prev => ({ ...prev, errorMsg: 'This organization already exists.' }));
            } else if (!validatingOrgName && isOrgNameValid) {
                setOrganizationName(prev => ({ ...prev, errorMsg: null }));
            }
        } else {
            setOrganizationName(prev => ({ ...prev, errorMsg: null }));
        }
    }, [isOrgNameValid, validatingOrgName, debouncedOrganizationName, userOrgLookupData?.org]);

    useEffect(() => {
        // Only show email validation error for new users - existing users with restricted emails are allowed
        if (userOrgLookupData?.emailValidationError && !userOrgLookupData?.user) {
            setEmail(prev => ({
                ...prev,
                errorMsg: userOrgLookupData.emailValidationError?.message || 'Email domain not allowed',
            }));
        }
    }, [userOrgLookupData?.emailValidationError, userOrgLookupData?.user]);

    const signUpEnabled: boolean = Boolean(
        email.value
        && jobTitle.value
        && (organizationName.value || userOrgLookupData?.org)
        && name.value
        && hasAcceptedTerms
        && validCaptcha
        && (isOrgNameValid || userOrgLookupData?.org)
        && !email.errorMsg
        && !jobTitle.errorMsg
        && (!organizationName.errorMsg || userOrgLookupData?.org)
        && !name.errorMsg,
    );

    const socialSignUpEnabled = socialSignUpConnectionType === undefined; // disable if already returned from social sign-up

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setName({ value, errorMsg: null });
    };

    const handleJobTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setJobTitle({ value, errorMsg: null });
    };

    const handleOrganizationNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setOrganizationName({ value, errorMsg: null });
        setBlockSignUp(true);
    };

    useEffect(() => {
        if (signUpAuth0Data && signUpAuth0Data.email) {
            setSocialSignUpConnectionType(signUpAuth0Data?.sub?.split('|')?.[0] as SocialSignUpConnection);
            if (signUpAuth0Data.email && signUpAuth0Data.email !== email.value) {
                // Domain validation is done by backend - only block new users, allow existing users
                setEmail({ value: signUpAuth0Data.email, errorMsg: null });
            }
            if (signUpAuth0Data.given_name && signUpAuth0Data.given_name !== name.value) {
                setName({ value: signUpAuth0Data.given_name, errorMsg: null });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signUpAuth0Data?.email, signUpAuth0Data?.sub, signUpAuth0Data?.given_name]);
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        // Don't show error for empty string
        if (!value) {
            setEmail({ value, errorMsg: null });
            return;
        }

        const isValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

        if (!isValidFormat) {
            setEmail({ value, errorMsg: 'Invalid email format' });
            return;
        }

        setEmail({ value, errorMsg: null });
    };

    const handleSignupValidation = async (): Promise<boolean> => {
        try {
            if (!executeRecaptcha) {
                console.error('Execute recaptcha not yet available');
                return false;
            }

            const token = await executeRecaptcha('signUp');
            if (!token) {
                console.error('Recaptcha token not received');
                return false;
            }

            const result = await validateSignUpUserAndOrg({
                organizationName: userOrgLookupData?.org?.name || organizationName.value,
                email: email.value,
                captchaToken: token,
            });

            if (!result) {
                return false;
            }

            const isValid = result.validEmail && (result.validOrgName || !!userOrgLookupData?.org) && result.validCaptcha;

            if (!result.validEmail) {
                setEmail(prev => ({ ...prev, errorMsg: 'Email already exists' }));
            }
            if (!result.validOrgName && !userOrgLookupData?.org) {
                setOrganizationName(prev => ({ ...prev, errorMsg: 'This organization already exists' }));
            }
            if (!result.validCaptcha) {
                setValidCaptcha(false);
            }

            return isValid;
        } catch (error) {
            console.error('Validation error:', error);
            return false;
        }
    };

    const handleSignUp = async () => {
        if (!signUpEnabled) return;

        const isValid = await handleSignupValidation();
        if (!isValid) return;

        const effectiveOrgName = userOrgLookupData?.org?.name || organizationName.value;

        signUpUserAndOrg({
            organizationName: effectiveOrgName,
            name: name.value,
            email: email.value,
            workEmail: email.value,
            termsAccepted: hasAcceptedTerms,
            redirectUrl: redirectUrlAfterPasswordReset,
            auth0UserProfile: signUpAuth0Data ? JSON.stringify(signUpAuth0Data) : undefined, // handles case where user is signing up via social login
        }, {
            onSuccess: () => {
                // For overlay flow that redirects to /account_created, set stage to ACCOUNT_CREATED
                // This ensures the AccountCreated page won't redirect away when user lands there
                // SignupOverlay will still show EmailVerificationPage for this stage
                setSignUpAuth0Data({}); // Clear stored auth0 data after successful sign-up
                if (redirectUrlAfterPasswordReset?.includes('/account_created')) {
                    if (!isDirectSignUpFlow) {
                        setCurrentStage(SignUpStage.ACCOUNT_CREATED);
                    }
                    updateStageData({ submitted: true }, SignUpStage.ACCOUNT_CREATED);
                } else if (socialSignUpConnectionType) {
                    if (!isDirectSignUpFlow) {
                        setCurrentStage(SignUpStage.ACCOUNT_CREATED);
                    }
                    window.location.replace(URLS.DEALS.HOME); // redirect to deals home. redirect handler on app level will handle account created use case
                } else {
                    // For direct signup flow, show email verification
                    setCurrentStage(SignUpStage.EMAIL_VERIFICATION);
                    updateStageData({ email: email.value }, SignUpStage.EMAIL_VERIFICATION);
                }
            },
            onError: (error: Error) => {
                const axiosError = error as AxiosError<{ detail?: { error_code?: string } }>;
                const errorData = axiosError?.response?.data?.detail;
                if (errorData?.error_code === PERSONAL_EMAIL_NOT_ALLOWED_CODE) {
                    setEmail(prev => ({ ...prev, errorMsg: PERSONAL_EMAIL_NOT_ALLOWED_MESSAGE }));
                } else if (errorData?.error_code === BLOCKED_DOMAIN_CODE) {
                    setEmail(prev => ({ ...prev, errorMsg: BLOCKED_DOMAIN_MESSAGE }));
                } else {
                    console.error('Signup error:', error);
                }
            },
        });
    };

    const handleSocialSignUp = (connection: SocialSignUpConnection) => {
        if (socialSignUpEnabled) {
            loginWithRedirect({
                authorizationParams: {
                    connection,
                    prompt: 'login',
                    screen_hint: 'signup',
                },
                appState: {
                    isDirectSignUpFlow,
                    signupType: SignUpType.SOCIAL,
                },
            });
        }
    };

    return {
        signUpEnabled,
        socialSignUpEnabled,
        email,
        jobTitle,
        organizationName,
        name,
        hasAcceptedTerms,
        validCaptcha,
        isOrgNameValid,
        orgValidationSuccess,
        userOrgLookupData,
        redirectUrlAfterPasswordReset,
        socialSignUpConnectionType,
        setHasAcceptedTerms,
        setRedirectUrlAfterPasswordReset,
        handleEmailChange,
        handleJobTitleChange,
        handleOrganizationNameChange,
        handleNameChange,
        handleSignUp,
        handleSocialSignUp,
        resetSignUpState,
    };
};

export default useSignUpManager;
